import React, { useState, useMemo } from 'react';
import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Chip, IconButton, Collapse,
    FormControlLabel, Switch, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { tokens } from '../../../../theme';
import { sessionStatusColor, formatStudentName, isSessionDone, sessionScore, compareAttempts } from './resultUtils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);

// Score lives on the session (combined auto-graded + any FRQ), so we read it directly.
const fmt = (s) => { const { raw, total } = sessionScore(s); return total ? `${raw}/${total}` : '—'; };
const pct = (s) => sessionScore(s).pct;
const pctStr = (p) => (p == null ? '—' : `${Math.round(p * 100)}%`);

/**
 * Admin results view for retakeable tests: one summary row per student
 * (#attempts, best, latest, average) that expands to the individual attempts,
 * each drilling into its own result page.
 */
export default function RetakeResultSummary({
    courseUuid, courseTestId, courseTest, test, sessions, notStartedStudents,
    isAdmin, releaseGrades, onToggleRelease, navigate,
}) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [expanded, setExpanded] = useState(null); // student_id

    // Group sessions (attempts) by student. Memoized — only recompute when sessions change.
    const rows = useMemo(() => {
        const groups = {};
        for (const s of sessions) (groups[s.student_id] = groups[s.student_id] || []).push(s);
        return Object.values(groups).map((group) => {
            const attempts = [...group].sort(compareAttempts); // oldest → newest
            const completed = attempts.filter(isSessionDone);
            const latest = attempts[attempts.length - 1];
            const pcts = completed.map(pct).filter((p) => p != null);
            let best = null;
            for (const s of completed) if (pct(s) != null && (best == null || pct(s) > pct(best))) best = s;
            // Average over completed attempts that actually have a score (excludes
            // any not-yet-scored attempt, consistent with `best`).
            const avg = pcts.length ? pcts.reduce((a, p) => a + p, 0) / pcts.length : null;
            const student = group.find((s) => s.student)?.student;
            const name = formatStudentName(student) || `Student #${group[0].student_id}`;
            return { student_id: group[0].student_id, name, attempts, completed, latest, best, avg };
        }).sort((a, b) => a.name.localeCompare(b.name));
    }, [sessions]);

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <IconButton onClick={() => navigate(`/classrooms/${courseUuid}/tests`)}><ArrowBackIcon /></IconButton>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" fontWeight={600}>{courseTest?.title || test?.title} — Attempts</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {rows.length} student{rows.length === 1 ? '' : 's'} attempted, {notStartedStudents.length} not started
                        {test?.questions_per_attempt ? ` · ${test.questions_per_attempt} questions per attempt` : ''}
                    </Typography>
                </Box>
                {isAdmin && <>
                    <Button size="small" variant="outlined" startIcon={<VisibilityIcon />}
                        onClick={() => navigate(`/classrooms/${courseUuid}/test-v2/${courseTestId}?preview=true`)}
                        sx={{ borderColor: colors.blueAccent[600], color: colors.blueAccent[600] }}>
                        Preview
                    </Button>
                    <FormControlLabel
                        control={<Switch checked={releaseGrades} onChange={onToggleRelease} />}
                        label="Release grades to students"
                    />
                </>}
            </Box>

            <TableContainer component={Paper} sx={{ bgcolor: colors.primary[600] }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 40 }} />
                            <TableCell><b>Student</b></TableCell>
                            <TableCell align="center"><b>Attempts</b></TableCell>
                            <TableCell align="center"><b>Best</b></TableCell>
                            <TableCell align="center"><b>Latest</b></TableCell>
                            <TableCell align="center"><b>Average</b></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && notStartedStudents.length === 0 ? (
                            <TableRow><TableCell colSpan={6} align="center"><Typography color="text.secondary" py={4}>No students assigned</Typography></TableCell></TableRow>
                        ) : <>
                            {rows.map((r) => {
                                const isOpen = expanded === r.student_id;
                                const latestDone = isSessionDone(r.latest);
                                return (
                                    <React.Fragment key={r.student_id}>
                                        <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpanded(isOpen ? null : r.student_id)}>
                                            <TableCell><IconButton size="small">{isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton></TableCell>
                                            <TableCell>{r.name}</TableCell>
                                            <TableCell align="center">{r.attempts.length}</TableCell>
                                            <TableCell align="center">{r.best ? <b>{fmt(r.best)}</b> : '—'}</TableCell>
                                            <TableCell align="center">{latestDone ? fmt(r.latest) : <Chip label={r.latest?.status || '—'} size="small" color={sessionStatusColor(r.latest?.status)} />}</TableCell>
                                            <TableCell align="center">{pctStr(r.avg)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ py: 0, border: isOpen ? undefined : 'none' }}>
                                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                    <Box sx={{ m: 1 }}>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell><b>Attempt</b></TableCell>
                                                                    <TableCell align="center"><b>Submitted</b></TableCell>
                                                                    <TableCell align="center"><b>Status</b></TableCell>
                                                                    <TableCell align="center"><b>Score</b></TableCell>
                                                                    <TableCell align="center" />
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {[...r.attempts].reverse().map((a) => (
                                                                    <TableRow key={a.id} hover sx={{ cursor: 'pointer' }}
                                                                        onClick={() => navigate(`/classrooms/${courseUuid}/test-v2/${courseTestId}/result/${a.id}`)}>
                                                                        <TableCell>#{a.attempt_number || 1}</TableCell>
                                                                        <TableCell align="center" sx={{ fontSize: 12, color: 'text.secondary' }}>
                                                                            {a.end_time ? dayjs(a.end_time).tz('America/New_York').format('M/D/YY h:mma') : '—'}
                                                                        </TableCell>
                                                                        <TableCell align="center"><Chip label={a.status} size="small" color={sessionStatusColor(a.status)} /></TableCell>
                                                                        <TableCell align="center">{fmt(a)}</TableCell>
                                                                        <TableCell align="center"><Button size="small">View</Button></TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                            {notStartedStudents.map((st) => (
                                <TableRow key={`ns-${st.id}`} sx={{ opacity: 0.6 }}>
                                    <TableCell />
                                    <TableCell>{formatStudentName(st)}</TableCell>
                                    <TableCell align="center">0</TableCell>
                                    <TableCell align="center">—</TableCell>
                                    <TableCell align="center"><Chip label="not started" size="small" variant="outlined" /></TableCell>
                                    <TableCell align="center">—</TableCell>
                                </TableRow>
                            ))}
                        </>}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
